import React from 'react';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import usePageMeta from '../hooks/usePageMeta';

function Terms() {
  usePageMeta({
    title: 'Terms & Conditions | WearTwice',
    description: 'The Terms and Conditions governing use of the WearTwice marketplace for buying and selling pre-loved fashion in the UAE.',
    path: '/users-terms-conditions',
  });

  return (
    <>
      <section className="relative w-full h-auto bg-[#47715B] p-8">
      <div className="max-w-3xl mx-auto bg-[#47715B] rounded-lg p-6 text-white">
        <h1 className="text-2xl font-bold mb-4">Weartwice - User Terms and Conditions</h1>
        <p className="text-gray-300">Effective Date – 1 October 2024</p>

        <div className="content">
          <p className="mt-4">
            These Weartwice Platform Terms of Use (these <strong>"Terms of Use"</strong>) tell you
            the rules for using the platform available at{' '}
            <a href="https://www.weartwice.com/" className="text-blue-300 underline">www.weartwice.com</a> and through any Weartwice
            mobile application available from time to time (collectively, the <strong>"Platform"</strong>),
            as well as any information, content, or materials published on, or available via, the
            Platform (collectively, the <strong>"Content"</strong>).
          </p>
          <p className="mt-4">
            These Terms of Use govern and condition access to, and use of, the Platform and its
            Content by each visitor to this Platform (<strong>"you"</strong>).
          </p>

          <h2 className="text-xl font-bold mt-6">WHO WE ARE AND HOW TO CONTACT US</h2>
          <p className="mt-4">
            The Platform is operated and owned by WEARTWICE ("Weartwice", "we", "our", "us").<br />
            Our mailing address is:
          </p>
          <address className="mt-2">
            IFZA Business Park, Building A2<br />
            Dubai Silicon Oasis, Dubai, PO BOX 342001<br />
            United Arab Emirates
          </address>
          <p className="mt-4">
            To contact us about these Terms of Use or anything else relating to your use of this
            Platform, please email <a href="mailto:support@weartwice.com" className="text-blue-300 underline">support@weartwice.com</a>.
          </p>

          <h2 className="text-xl font-bold mt-6">BY USING THE PLATFORM, YOU ACCEPT THESE TERMS OF USE</h2>
          <p className="mt-4">
            By accessing and continuing to use the Platform, you confirm that you accept these
            Terms of Use, and acknowledge and agree that these Terms of Use govern and condition
            your access to, and use of, the Platform and any Content.
          </p>
          <p className="mt-4">
            You understand that these Terms of Use constitute a legally binding agreement between
            you and Weartwice.
          </p>
          <p className="mt-4">
            If you are using the Platform as a business entity or on behalf of a business entity,
            you represent that you have the authority to legally bind that entity.
          </p>
          <p className="mt-4">
            If you do not agree to be bound by these Terms of Use, you may not access or use the
            Platform or any Content and should cease to do so immediately.
          </p>

          <h2 className="text-xl font-bold mt-6">WE MAY MAKE CHANGES TO THESE TERMS OF USE</h2>
          <p className="mt-4">
            We may amend these Terms of Use from time to time, with or without notice to you. The
            new version of these Terms of Use will be made available on this page. If you are an
            existing registered user, we may also notify you of any material update to these Terms
            of Use via email.
          </p>

          <h2 className="text-xl font-bold mt-6">WE MAY MAKE CHANGES TO THE PLATFORM</h2>
          <p className="mt-4">
            We may change the way the Platform operates and/or change any Content from time to
            time without notice to you. This could be, for example, to reflect changes to the
            features and functionalities of the Platform, the state of current technology, or
            market practice, applicable laws or regulations and/or our business priorities.
          </p>

          <h2 className="text-xl font-bold mt-6">WE MAY SUSPEND OR WITHDRAW THE PLATFORM</h2>
          <p className="mt-4">
            We do not guarantee that the Platform and/or any Content will always be available free
            of charge or generally available. We may suspend or withdraw, or restrict the
            availability of, all or any part of the Platform for business and/or operational reasons.
          </p>

          <h2 className="text-xl font-bold mt-6">YOUR PRIVACY</h2>
          <p className="mt-4">
            Please refer to our <Link to="/privacy-policy" className="text-blue-300 underline">Privacy Policy</Link> to understand how
            we collect, process, and share your personal data in relation to your use of the Platform.
          </p>

          <h2 className="text-xl font-bold mt-6">YOUR RIGHT TO USE THE PLATFORM AND THE CONTENT</h2>
          <p className="mt-4">
            Subject to your continued compliance with these Terms of Use, Weartwice grants you a
            personal, limited, non-transferable, non-exclusive, revocable right to use the Platform
            and the Content.
          </p>
          <p className="mt-4">
            Weartwice is the owner or the licensee of all rights (including intellectual property
            rights) in the Platform and any Content. The Platform and the Content are protected by
            copyright laws and treaties around the world. All such rights are reserved.
          </p>

          <h2 className="text-xl font-bold mt-6">RESTRICTIONS</h2>
          <div className="text-left pl-5 space-y-2">
            <p className="font-semibold">When creating an Account or using the Site or Services, you agree you will not :</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>
                Do anything illegal, unethical, contrary to public security or that infringes our
                or someone else's rights
              </li>
              <li>
                Use any kind of external software tools (including but not limited to: bots,
                scraping programs, crawling programs, spiders) when registering on the Site
              </li>
              <li>
                Publish Content that praises, promotes, encourages or incites terrorism, racism,
                revisionism, xenophobia, homophobia, sexism, hate speech, discrimination, or other
                unlawful Content
              </li>
              <li>Delete and re-list the same Item multiple times or multiple Items in bulk</li>
              <li>
                Upload non-original or misleading photos, including images copied from the internet,
                screenshots, or brand/product photos not taken by the User
              </li>
              <li>Attempting to arrange or complete payment outside the Platform is strictly prohibited</li>
              <li>We will report any breach of the restrictions listed above to the relevant law enforcement authorities and will cooperate with those authorities by disclosing your identity to them.</li>
            </ul>
            <p className="font-semibold">No Swaps or Trades</p>
            <p className="mt-4">
              Swaps, trades, exchanges, gifts, or any non-monetary transactions are strictly prohibited. Only sales completed through Weartwice’s payment and shipping systems are permitted. Engaging in off-platform or non-monetary transactions prevents Weartwice from verifying delivery, protecting Buyers, or resolving disputes, and may result in corrective action.
            </p>
          </div>

          <h2 className="text-xl font-bold mt-6">ACCOUNT CREATION</h2>
          <p className="mt-4">
            Although you can browse the Platform as a guest, you will need to create an account
            (an "Account") in order to access certain features of the Platform.
          </p>

          <h2 className="text-xl font-bold mt-6">LISTINGS</h2>
          <p className="mt-4">
            The Platform enables a user wishing to sell an item or service (the "Seller") to
            advertise such item (each a "Listing") to other users of the Platform. A user who is
            interested in such an item or service (the "Buyer") may then contact the Seller (via
            the Listing) to make an offer for such item.
          </p>
          <div className="text-left pl-5 space-y-2">
            <p className="font-semibold">Authenticity & Intellectual Property</p>
            <p className="mt-4">
                Every seller represents and warrants that all items they list are authentic and do not infringe any third-party intellectual property rights (including trademarks, logos, and protected designs).
            </p>
            <p className="mt-4">Sellers must not upload, distribute, or sell any item that violates IP rights.</p>
          </div>
          <div className="text-left pl-5 space-y-2">
            <p className="font-semibold">Accuracy of Item Descriptions</p>
            <p className="mt-4">
                Sellers must provide a truthful, complete, and accurate description of each item. This includes the item’s actual condition, size, material, signs of wear, defects, alterations, and any feature that may influence the Buyer’s decision. Any omission or misleading description is strictly prohibited and may result in removal of the listing and further action.
            </p>
          </div>
          <div className="text-left pl-5 space-y-2">
            <p className="font-semibold">Photo Requirements</p>
            <p className="mt-4">
                All photos must be original and taken directly by the Seller using a mobile phone or camera. The use of images taken from the internet, screenshots, catalogue photos, brand visuals, or AI-generated content is strictly prohibited. Photos must show the item’s real condition and clearly display any flaws or imperfections. Misleading, heavily edited, or altered photos are not permitted.
            </p>
          </div>
          <div className="text-left pl-5 space-y-2">
            <p className="font-semibold">Correct Category Requirement</p>
            <p className="mt-4">
                Sellers must list each item in the correct category, sub-category, size, and brand field. Misclassification of items whether intentional or accidental may affect search accuracy and buyer experience and may result in the removal of the listing.
            </p>
          </div>

          <h2 className="text-xl font-bold mt-6">LISTINGS – SINGLE ITEM REQUIREMENT</h2>
          <p className="mt-4">
            Each Listing on the Platform must concern a single item, described and photographed on its own. Listings that show multiple distinct items together (for example a “lot” of several items sold as one) are not permitted unless explicitly allowed in a dedicated “bundle” format approved by Weartwice.
          </p>
          <p className="mt-4">
            Sellers may not upload a photo showing multiple different items or package lots as one listing. Offering multiple distinct items in one listing may lead to removal of the listing and/or suspension of the Seller account.
          </p>
          <div className="text-left pl-5 space-y-2">
            <p className="font-semibold">Items Not As Described (INAD)</p>
            <p className="mt-4">
                If an item received differs significantly from its description or photos—including condition, size, colour, defects, missing accessories, or any essential characteristic—the Buyer may submit an INAD claim. Weartwice will review the case and may request evidence from both parties. If the INAD claim is confirmed, the Buyer will be refunded and appropriate measures may be taken against the Seller.                
            </p>
          </div>

          <h2 className="text-xl font-bold mt-6">COUNTERFEIT ITEMS</h2>
          <p className="mt-4">
            Weartwice strictly prohibits the listing of counterfeit items and applies best-effort measures to prevent and detect such activity. While we do not carry out systematic authentication of items, we investigate any report or suspicion of counterfeit goods.
          </p>
          <p className="mt-4">
            If Weartwice concludes, following a thorough investigation, that an item is counterfeit, it may be retained or destroyed in accordance with applicable laws and brand-protection requirements. If a second confirmed case shows that a user has listed counterfeit items, their account will be permanently suspended and removed, with no possibility of reinstatement.
          </p>
          <p className="mt-4">
            Where appropriate, Weartwice may also report the matter to the relevant authorities or brand-protection teams.
          </p>
          <p className="mt-4">
            Weartwice cooperates with brand-protection teams, rights holders, and relevant authorities to report counterfeit activity.
          </p>
          <p className="mt-4">
            Weartwice may share user information where required by law for anti-counterfeiting investigations.
          </p>

          <h2 className="text-xl font-bold mt-6">BUYER PROTECTION – COUNTERFEIT ITEMS</h2>
          <p className="mt-4">
            Buyers must report any suspicion of a counterfeit item within 48 hours after delivery by submitting a claim through the Platform.
          </p>
          <p className="mt-4">
            Once the claim is submitted, Weartwice has up to 72 hours to review the case, request additional evidence from the buyer and/or seller, and reach a determination regarding authenticity.
          </p>
          <p className="mt-4">
            If Weartwice concludes that the item is counterfeit, the buyer will receive a full refund, and the seller will not be entitled to request the return of the item.
          </p>
          <p className="mt-4">
            Weartwice may require the buyer to provide supporting evidence such as photographs, receipts, certificates, or expert evaluations.
          </p>
          <p className="mt-4">
            Weartwice may retain, destroy, or handle the counterfeit item in accordance with applicable UAE laws and brand-protection requirements.
          </p>

          <h2 className="text-xl font-bold mt-6">ACCEPTABLE USE POLICY</h2>
          <p className="mt-4">
            Whenever you make use of a feature that allows you to upload content to the Platform,
            or to make contact with other users of the Platform, you must comply with the standards
            set out in our <Link to="/acceptable-use-policy" className="text-blue-300 underline">Acceptable Use Policy</Link>.
          </p>
          <div className="text-left pl-5 space-y-2">
            <p className="font-semibold">User Communications</p>
            <p className="mt-4">
                All interactions between Buyers and Sellers must take place exclusively through the tools and features provided by Weartwice. Sharing personal contact details, attempting to arrange payment or delivery outside the Platform, or redirecting users to external channels is strictly prohibited. Any bypass of the Platform’s processes may result in corrective action.
            </p>
          </div>
          <div className="text-left pl-5 space-y-2">
            <p className="font-semibold">User Behaviour Standards</p>
            <p className="mt-4">
                Users must act respectfully, responsibly, and in good faith when interacting through the Platform. Abusive, threatening, discriminatory, or otherwise inappropriate conduct is strictly prohibited and may lead to restrictions or suspension.
            </p>
          </div>

          <h2 className="text-xl font-bold mt-6">BREACH OF THESE TERMS OF USE</h2>
          <div className="text-left pl-5 space-y-2">
            <p className="font-semibold">Preventive Suspension</p>
            <p className="mt-4">
                Weartwice may temporarily suspend or permanently remove a listing or account when strong indications suggest a breach of our rules, including suspected fraud, repeated disputes, counterfeit activity, misleading behaviour, or any action that may compromise the safety or integrity of the Platform.
            </p>
          </div>
          <p className="mt-4">
            When we consider, in our sole opinion, that you are in breach of any part of these
            Terms of Use, we may take such action as we deem appropriate in our sole discretion,
            including without limitation suspending or withdrawing your right to use the Platform
            or its Content, closing your Account, and/or taking legal action against you.
          </p>

          <h2 className="text-xl font-bold mt-6">BLOCKING YOUR ACCOUNT</h2>
          <div className="text-left pl-5 space-y-2">
            <p className="font-semibold">We might block your Account temporarily or definitively if :</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>
                We took a corrective action listed above and you continue to repeatedly breach
                these Terms
              </li>
              <li>
                You commit a serious breach of these Terms, meaning:
                <ul className="list-[circle] pl-5 space-y-1">
                  <li>
                    You provide us with incorrect, false or misleading information on your Account
                  </li>
                  <li>
                    You don't cooperate with us when we try to verify information you provide on
                    your Account
                  </li>
                  <li>You abuse the Site's functionalities</li>
                </ul>
              </li>
            </ul>
          </div>
          <div className="text-left pl-5 space-y-2">
            <p className="font-semibold">Final Determinations</p>
            <p className="mt-4">
                For disputes, claims, authenticity assessments, and INAD reviews, Weartwice will make a final determination based on the evidence available. Decisions made by Weartwice under these rules are final and binding.
            </p>
          </div>
          <div className="text-left pl-5 space-y-2">
            <p className="font-semibold">Fees and Buyer Protection</p>
            <p className="font-semibold">For every purchase, the Buyer pays:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>
                The item price set by the Seller (with or without negotiation),
              </li>
              <li>
                A Buyer Protection Fee equal to 20% of the item price, and
              </li>
              <li>
                A fixed shipping fee of 20 AED per order, regardless of the number of items included.
              </li>
              <li> By completing a purchase, the Buyer accepts these fees.</li>
            </ul>
          </div>
          

          <h2 className="text-xl font-bold mt-6">DISPUTE RESOLUTION</h2>
          <p className="mt-4">
            If a dispute arises between you and Weartwice, we strongly encourage you to first
            contact us directly to seek a resolution by emailing support@weartwice.com. We will
            review your complaint and do our best to address it.
          </p>

          <h2 className="text-xl font-bold mt-6">GOVERNING LAW</h2>
          <p className="mt-4">
            These Terms of Service and any separate agreements whereby we provide you Services
            shall be governed by and construed in accordance with the laws of the United Arab Emirates.
          </p>
        </div>
      </div>

        <div className="flex justify-center mt-8">
          <Link
            to="/"
            className="bg-white text-[#47715B] px-4 py-2 rounded-lg shadow hover:bg-gray-200 transition duration-300"
          >
            Back to Home
          </Link>
        </div>
      </section>
      <Footer />
    </>
  );
}

export default Terms;
